"use client";

import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Button } from "@/components/ui/button-pro";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { ThemeMenu } from "./theme/theme-menu";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [mounted, setMounted] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setActiveSection(sectionId);

            // Only update route if we're on the home page
            if (pathname === "/") {
              // Use replace instead of push to avoid adding to history
              window.history.replaceState(null, "", `/#${sectionId}`);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [pathname, mounted]);

  const getNavItems = () => {
    const baseItems = [
      { href: pathname === "/" ? "/#home" : "/", label: "Home" },
      { href: "/about", label: "About" },
      { href: "/skills", label: "Skills" },
      { href: "/projects", label: "Projects" },
      { href: "/resume", label: "Resume" },
      { href: "/services", label: "Services" },
      { href: "/contact", label: "Contact" },
      { href: "/chatbot", label: "Chatbot" },
    ];

    return baseItems.map((item) => {
      if (item.href.startsWith("/#")) {
        const section = item.href.substring(2);
        if (activeSection === section) {
          return { ...item, href: `/#${section}` };
        }
      }
      return item;
    });
  };

  const navItems = getNavItems();

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return (
      <nav className="fixed top-0 w-full z-50 transition-all duration-300 bg-gray-200 dark:bg-gray-700 border-b">
        <div className="xl:mx-20 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div>
              <span className="text-xl lg:text-2xl cursor-pointer font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
                Hamza M.
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <header
      id="header"
      className={cn(
        "fixed w-full top-0 z-50 transition-all duration-300 border-b",
        !scrolled
          ? "bg-white dark:bg-black/40 backdrop-blur-md border-gray-200 dark:border-purple-500/20"
          : "bg-gray-300 dark:bg-black"
      )}
    >
      <div className="xl:mx-20 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div onClick={() => router.push("/")}>
            <span className="text-xl lg:text-2xl cursor-pointer font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              Hamza M.
            </span>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-baseline space-x-3">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 hover:bg-purple-500/10",
                    activeSection === item.href.substring(2) &&
                      "text-purple-600 dark:text-white"
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            {/* <ThemeToggle /> */}
            {/* <ThemeToggleList /> */}
            <ThemeMenu />
          </div>
          <div className="md:hidden flex items-center space-x-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-white"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden bg-white/95 dark:bg-black/95 backdrop-blur-md">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={cn(
                  "text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-white block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200",
                  activeSection === item.href.substring(2) &&
                    "text-purple-600 dark:text-white"
                )}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
